import React from "react";
import { Typography, Button, Container, Box, Grid, Paper } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Link } from "@mui/material";
import { DialogContext, TranslationContext } from "./context";
import {
  interactiveCalculatorInputBoxColor,
  settingsAccordionDetails,
  settingsInInteractiveCalculator,
} from "./constants";

// Styling for the components
const useStyles = makeStyles({
  hero: {
    backgroundColor: "transparent",
    padding: "30px 0",
    textAlign: "center",
    borderRadius: "20px",
  },
  heroText: {
    marginBottom: "20px",
  },
  section: {
    padding: "20px 0",
  },
  sectionLast: {
    padding: "20px 0 40px 0",
  },
  sectionTitle: {
    marginBottom: "20px",
  },
  testimonialCard: {
    padding: "20px",
    height: "100%",
    borderRadius: "16px", // Apply rounded corners
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)", // Optional for card shadow
    display: "flex", // Flexbox for centering
    justifyContent: "center", // Horizontally centered
    alignItems: "center", // Vertically centered
    textAlign: "center", // Ensure the text is centered
  },
  footer: {
    backgroundColor: "#333",
    color: "#fff",
    padding: "30px 0",
    textAlign: "center",
  },
  heroBox: {
    padding: "30px",
    backgroundColor: "rgba(255, 255, 255, 0.8)", // Transparent white
    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)", // slight elevation
    borderRadius: "20px",
  },
});

const LandingPage = () => {
  const classes = useStyles();

  const { DialogDispatcher } = React.useContext(DialogContext); // Access the DialogDispatcher

  // Function to open the signup dialog
  const handleSignUpClick = () => {
    DialogDispatcher("OPEN", { signup: true }); // Dispatch the signup dialog
  };

  const t = React.useContext(TranslationContext);

  return (
    <div>
      {/* Hero Section */}
      <Box className={classes.hero}>
        <Container>
          <Paper
            className={classes.heroBox}
            elevation={3}
            sx={{
              borderRadius: "20px",
              backgroundColor: interactiveCalculatorInputBoxColor,
            }}
          >
            <Typography
              variant="h4"
              component="h1"
              className={classes.heroText}
              marginBottom="2vh"
              fontWeight="bold"
            >
              {t("Unlock Your Interactive Kundali with AstroData")}
            </Typography>
            <Typography variant="h5" component="p">
              {t(
                "AstroData brings the power of astrological insights to your fingertips with interactive, personalized Kundali readings."
              )}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              sx={{ mt: 4 }}
              href="/interactive-kundali"
            >
              {t("Get Started")}
            </Button>
          </Paper>
        </Container>
      </Box>

      {/* Features Section */}
      <Box className={classes.section} id="features">
        <Container>
          <Paper
            className={classes.heroBox}
            elevation={3}
            sx={{
              borderRadius: "20px",
              backgroundColor: interactiveCalculatorInputBoxColor,
            }}
          >
            <Typography
              variant="h4"
              component="h2"
              sx={{ marginBottom: "3vh" }}
              className={classes.sectionTitle}
            >
              Why Choose AstroData?
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  className={classes.testimonialCard}
                  sx={{
                    borderRadius: "16px",
                    backgroundColor: settingsAccordionDetails,
                  }}
                >
                  <Typography variant="h6">
                    Interactive Kundali Experience
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  className={classes.testimonialCard}
                  sx={{
                    borderRadius: "16px",
                    backgroundColor: settingsAccordionDetails,
                  }}
                >
                  <Typography variant="h6">
                    Accurate Astrological Calculations
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  className={classes.testimonialCard}
                  sx={{
                    borderRadius: "16px",
                    backgroundColor: settingsAccordionDetails,
                  }}
                >
                  <Typography variant="h6">
                    Predictions based on Research
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Paper
                  className={classes.testimonialCard}
                  sx={{
                    borderRadius: "16px",
                    backgroundColor: settingsAccordionDetails,
                  }}
                >
                  <Typography variant="h6">Guarantee on Accuracy</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box
        className={classes.section}
        id="testimonials"
        style={{ backgroundColor: "transparent" }}
      >
        <Container>
          <Paper
            className={classes.heroBox}
            elevation={3}
            sx={{
              borderRadius: "20px",
              backgroundColor: interactiveCalculatorInputBoxColor,
            }}
          >
            <Typography
              variant="h4"
              component="h2"
              className={classes.sectionTitle}
              sx={{ marginBottom: "16px" }}
            >
              What Our Users Are Saying
            </Typography>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6}>
                <Paper
                  className={classes.testimonialCard}
                  sx={{
                    borderRadius: "16px",
                    backgroundColor: settingsInInteractiveCalculator,
                  }}
                >
                  <Typography variant="h6">
                    "AstroData gave me insights I never expected! The
                    interactive Kundali is a game-changer."
                  </Typography>
                  <Typography variant="body1"> Ravi</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper
                  className={classes.testimonialCard}
                  sx={{
                    borderRadius: "16px",
                    backgroundColor: settingsInInteractiveCalculator,
                  }}
                >
                  <Typography variant="h6">
                    "I've used other astrology services, but nothing compares to
                    the depth and ease of AstroData."
                  </Typography>
                  <Typography variant="body1"> Sam</Typography>
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>

      {/* Call to Action Section */}
      <Box className={classes.sectionLast} id="cta">
        <Container>
          <Paper
            className={classes.heroBox}
            elevation={3}
            sx={{
              borderRadius: "20px",
              backgroundColor: interactiveCalculatorInputBoxColor,
            }}
          >
            <Typography
              variant="h4"
              component="h2"
              className={classes.sectionTitle}
            >
              Ready to Dive Into Your Kundali?
            </Typography>
            <Typography
              variant="body1"
              component="p"
              style={{ marginBottom: "30px" }}
            >
              Join thousands of users who are uncovering their astrological
              potential with AstroData.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSignUpClick}
              sx={{
                marginLeft: { xs: "auto", sm: "auto", md: "0" }, // Center on mobile, left on desktop
                marginRight: { xs: "auto", sm: "auto", md: "0" }, // Ensure it's not pushed right on desktop
                display: "block", // Block-level to respect margin auto
              }}
            >
              Start Your Kundali Journey
            </Button>
          </Paper>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        className={classes.footer}
        sx={{ width: "100%", position: "relative" }}
      >
        <Typography variant="body1">
          © 2024 AstroData |
          <Link
            href="/privacy-policy"
            color="inherit"
            underline="hover"
            sx={{ mx: 1 }}
          >
            Privacy Policy
          </Link>
          |
          <Link
            href="/terms-and-conditions"
            color="inherit"
            underline="hover"
            sx={{ mx: 1 }}
          >
            Terms of Service
          </Link>
        </Typography>
      </Box>
    </div>
  );
};

export default LandingPage;
